import {
  Badge,
  Box,
  Button,
  Card,
  ColorSwatch,
  Container,
  Group,
  ScrollArea,
  Select,
  SimpleGrid,
  Stack,
  Text,
  TextInput,
  Title,
} from "@mantine/core";
import { IconCheck, IconCopy } from "@tabler/icons-react";
import { useEffect, useMemo, useState } from "react";
import {
  DEFAULT_FURNITURE_CATALOG_CATEGORY_KEY,
  listCollectionsForCategory,
  resolveCatalogCategoryName,
  resolveCatalogCollectionName,
} from "../../shared/furniture-catalog";
import type { DrobbitAnimationMode } from "../lib/drobbit-model";
import type {
  Session,
  UserProfileDetail,
  UserProfileSummary,
} from "../lib/api";
import {
  fetchProfileByAddress,
  fetchProfiles,
} from "../lib/api";
import {
  AVATAR_COLOR_OPTIONS,
  AvatarPreviewCanvas,
} from "../components/AvatarPreviewCanvas";
import { FurniturePreviewCanvas } from "../components/FurniturePreviewCanvas";

interface Props {
  session: Session;
  onSave: (name: string, color: string) => Promise<void>;
}

export function ProfileScreen({ session, onSave }: Props) {
  const [name, setName] = useState(session.displayName);
  const [color, setColor] = useState(session.avatarColor);
  const [saving, setSaving] = useState(false);
  const [saved, setSaved] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [copied, setCopied] = useState(false);
  const [previewAnim, setPreviewAnim] = useState<DrobbitAnimationMode>("idle");

  const [profiles, setProfiles] = useState<UserProfileSummary[]>([]);
  const [profilesLoading, setProfilesLoading] = useState(false);
  const [profilesError, setProfilesError] = useState<string | null>(null);
  const [profileSearch, setProfileSearch] = useState("");
  const [selectedProfileAddress, setSelectedProfileAddress] = useState(
    session.address
  );
  const [selectedProfile, setSelectedProfile] = useState<UserProfileDetail | null>(
    null
  );
  const [selectedProfileLoading, setSelectedProfileLoading] = useState(false);
  const [selectedProfileError, setSelectedProfileError] = useState<string | null>(
    null
  );
  const [profileCategoryFilter, setProfileCategoryFilter] = useState<string>("");
  const [profileCollectionFilter, setProfileCollectionFilter] = useState<string>("");

  useEffect(() => {
    setName(session.displayName);
    setColor(session.avatarColor);
    setSelectedProfileAddress((prev) => prev || session.address);
  }, [session.address, session.avatarColor, session.displayName]);

  useEffect(() => {
    const timer = window.setInterval(() => {
      setPreviewAnim("wave");
      window.setTimeout(() => setPreviewAnim("idle"), 1400);
    }, 6000);
    return () => window.clearInterval(timer);
  }, []);

  const refreshProfiles = async () => {
    setProfilesLoading(true);
    setProfilesError(null);
    try {
      const next = await fetchProfiles(session);
      setProfiles(next);
      if (!next.some((entry) => entry.address === selectedProfileAddress)) {
        setSelectedProfileAddress(session.address);
      }
    } catch (e) {
      setProfilesError(
        e instanceof Error ? e.message : "Failed to load profiles."
      );
    } finally {
      setProfilesLoading(false);
    }
  };

  useEffect(() => {
    void refreshProfiles();
  }, [session.address, session.token]);

  useEffect(() => {
    let cancelled = false;
    const loadProfile = async () => {
      setSelectedProfileLoading(true);
      setSelectedProfileError(null);
      try {
        const detail = await fetchProfileByAddress(session, selectedProfileAddress);
        if (cancelled) {
          return;
        }
        setSelectedProfile(detail);
      } catch (e) {
        if (cancelled) {
          return;
        }
        setSelectedProfile(null);
        setSelectedProfileError(
          e instanceof Error ? e.message : "Failed to load selected profile."
        );
      } finally {
        if (!cancelled) {
          setSelectedProfileLoading(false);
        }
      }
    };
    void loadProfile();
    return () => {
      cancelled = true;
    };
  }, [selectedProfileAddress, session.address, session.token]);

  const save = async () => {
    const trimmed = name.trim().slice(0, 30);
    if (!trimmed) {
      setError("Display name is required.");
      return;
    }
    setSaving(true);
    setError(null);
    try {
      await onSave(trimmed, color);
      setSaved(true);
      setTimeout(() => setSaved(false), 2000);
      await refreshProfiles();
      setSelectedProfileAddress(session.address);
    } catch (e: unknown) {
      setError(e instanceof Error ? e.message : "Failed to save profile.");
    } finally {
      setSaving(false);
    }
  };

  const copyAddress = () => {
    void navigator.clipboard.writeText(session.address);
    setCopied(true);
    setTimeout(() => setCopied(false), 1500);
  };

  const filteredProfiles = useMemo(() => {
    const q = profileSearch.trim().toLowerCase();
    if (!q) {
      return profiles;
    }
    return profiles.filter((entry) => {
      const hay = `${entry.displayName} ${entry.address}`.toLowerCase();
      return hay.includes(q);
    });
  }, [profileSearch, profiles]);

  const profileCategoryOptions = useMemo(() => {
    if (!selectedProfile) {
      return [];
    }
    const keySet = new Set<string>();
    for (const item of selectedProfile.furniture) {
      keySet.add(item.catalogCategory ?? DEFAULT_FURNITURE_CATALOG_CATEGORY_KEY);
    }
    return Array.from(keySet).map((key) => ({
      value: key,
      label: resolveCatalogCategoryName(key),
    }));
  }, [selectedProfile]);

  const profileCollectionOptions = useMemo(() => {
    if (!selectedProfile || !profileCategoryFilter) {
      return [];
    }
    const validCollectionKeys = new Set<string>();
    for (const item of selectedProfile.furniture) {
      const category = item.catalogCategory ?? DEFAULT_FURNITURE_CATALOG_CATEGORY_KEY;
      if (category !== profileCategoryFilter) {
        continue;
      }
      const key = item.catalogCollection?.trim();
      if (key) {
        validCollectionKeys.add(key);
      }
    }
    const preferred = listCollectionsForCategory(profileCategoryFilter)
      .filter((entry) => validCollectionKeys.has(entry.key))
      .map((entry) => ({
        value: entry.key,
        label: entry.name,
      }));
    const remaining = Array.from(validCollectionKeys)
      .filter((key) => !preferred.some((entry) => entry.value === key))
      .map((key) => ({
        value: key,
        label: resolveCatalogCollectionName(key),
      }));
    return [...preferred, ...remaining];
  }, [profileCategoryFilter, selectedProfile]);

  const filteredFurniture = useMemo(() => {
    if (!selectedProfile) {
      return [];
    }
    return selectedProfile.furniture.filter((item) => {
      const category = item.catalogCategory ?? DEFAULT_FURNITURE_CATALOG_CATEGORY_KEY;
      const collection = item.catalogCollection?.trim() ?? "";
      if (profileCategoryFilter && category !== profileCategoryFilter) {
        return false;
      }
      if (profileCollectionFilter && collection !== profileCollectionFilter) {
        return false;
      }
      return true;
    });
  }, [profileCategoryFilter, profileCollectionFilter, selectedProfile]);

  useEffect(() => {
    setProfileCollectionFilter("");
  }, [profileCategoryFilter, selectedProfileAddress]);

  return (
    <Container size="xl" py="md">
      <Stack gap="md">
        <Title order={2}>Profiles</Title>
        <SimpleGrid cols={{ base: 1, lg: 2 }} spacing="md">
          <Card
            p="lg"
            radius="md"
            style={{
              border: "1px solid rgba(0, 0, 0, 0.08)",
              minHeight: 640,
            }}
          >
            <Stack gap="md">
              <Text fw={600}>My Profile</Text>
              <Box
                style={{
                  height: 260,
                  borderRadius: 16,
                  overflow: "hidden",
                  border: "1px solid rgba(0, 0, 0, 0.06)",
                  background:
                    "radial-gradient(ellipse at 50% 80%, rgba(253,126,20,0.04) 0%, transparent 60%), linear-gradient(180deg, #edf2f7 0%, #f8f9fa 100%)",
                }}
              >
                <AvatarPreviewCanvas color={color} animationMode={previewAnim} />
              </Box>
              <Group
                gap={6}
                style={{
                  cursor: "pointer",
                  padding: "6px 12px",
                  borderRadius: 8,
                  background: "rgba(0, 0, 0, 0.03)",
                  border: "1px solid rgba(0, 0, 0, 0.06)",
                  alignSelf: "flex-start",
                }}
                onClick={copyAddress}
              >
                <Box
                  w={6}
                  h={6}
                  style={{
                    borderRadius: "50%",
                    background: color || "#ff922b",
                    flexShrink: 0,
                  }}
                />
                <Text size="xs" c="gray.6" ff="monospace" lh={1}>
                  {session.address.slice(0, 8)}...{session.address.slice(-6)}
                </Text>
                {copied ? (
                  <IconCheck size={12} color="#2dd4bf" />
                ) : (
                  <IconCopy size={12} color="rgba(0,0,0,0.3)" />
                )}
              </Group>

              <TextInput
                label="Display Name"
                value={name}
                onChange={(event) => setName(event.currentTarget.value)}
                maxLength={30}
              />

              <Box>
                <Text size="xs" c="gray.6" fw={500} mb={8}>
                  Avatar Color
                </Text>
                <SimpleGrid cols={5} spacing={8}>
                  {AVATAR_COLOR_OPTIONS.map((candidate) => (
                    <ColorSwatch
                      key={candidate}
                      color={candidate}
                      size={36}
                      onClick={() => setColor(candidate)}
                      style={{
                        cursor: "pointer",
                        outline:
                          color === candidate
                            ? "2px solid #ff922b"
                            : candidate === "#ffffff"
                            ? "1px solid rgba(0,0,0,0.15)"
                            : "none",
                        outlineOffset: 3,
                        margin: "0 auto",
                        transition: "transform 0.1s ease",
                        transform: color === candidate ? "scale(1.1)" : "scale(1)",
                      }}
                    />
                  ))}
                </SimpleGrid>
              </Box>

              {error && (
                <Text c="red.7" size="sm">
                  {error}
                </Text>
              )}

              <Button
                fullWidth
                size="md"
                loading={saving}
                onClick={() => void save()}
                color={saved ? "teal" : undefined}
              >
                {saved ? "Saved" : "Save Changes"}
              </Button>
            </Stack>
          </Card>

          <Card
            p="lg"
            radius="md"
            style={{
              border: "1px solid rgba(0, 0, 0, 0.08)",
              minHeight: 640,
            }}
          >
            <Stack gap="md" h="100%">
              <Group justify="space-between" align="end">
                <Text fw={600}>User Directory</Text>
                <Badge color={profilesLoading ? "yellow" : "gray"} variant="light">
                  {profilesLoading ? "Loading" : `${profiles.length} users`}
                </Badge>
              </Group>
              <TextInput
                placeholder="Search users"
                value={profileSearch}
                onChange={(event) => setProfileSearch(event.currentTarget.value)}
              />
              {profilesError ? (
                <Text size="sm" c="red">
                  {profilesError}
                </Text>
              ) : null}

              <Group align="start" grow style={{ minHeight: 0, flex: 1 }}>
                <ScrollArea h={230}>
                  <Stack gap={6}>
                    {filteredProfiles.map((entry) => (
                      <Button
                        key={entry.address}
                        variant={
                          entry.address === selectedProfileAddress ? "filled" : "light"
                        }
                        color={
                          entry.address === selectedProfileAddress ? "orange" : "gray"
                        }
                        justify="space-between"
                        onClick={() => setSelectedProfileAddress(entry.address)}
                      >
                        {entry.displayName}
                      </Button>
                    ))}
                  </Stack>
                </ScrollArea>

                <Stack gap="xs" style={{ minWidth: 0 }}>
                  {selectedProfileLoading ? (
                    <Text size="sm" c="dimmed">
                      Loading profile...
                    </Text>
                  ) : selectedProfileError ? (
                    <Text size="sm" c="red">
                      {selectedProfileError}
                    </Text>
                  ) : selectedProfile ? (
                    <>
                      <Text fw={700}>{selectedProfile.displayName}</Text>
                      <Text size="xs" c="dimmed" ff="monospace">
                        {selectedProfile.address}
                      </Text>
                      <Group gap={6}>
                        {selectedProfile.isAdmin ? (
                          <Badge size="xs" variant="light" color="teal">
                            Admin
                          </Badge>
                        ) : null}
                        <Badge size="xs" variant="outline">
                          Published {selectedProfile.publishedFurnitureCount}
                        </Badge>
                        {typeof selectedProfile.draftFurnitureCount === "number" ? (
                          <Badge size="xs" variant="outline" color="orange">
                            Drafts {selectedProfile.draftFurnitureCount}
                          </Badge>
                        ) : null}
                      </Group>
                    </>
                  ) : (
                    <Text size="sm" c="dimmed">
                      Select a user profile.
                    </Text>
                  )}
                </Stack>
              </Group>

              {selectedProfile ? (
                <Stack gap="xs">
                  <Group grow>
                    <Select
                      placeholder="Category"
                      clearable
                      value={profileCategoryFilter || null}
                      data={profileCategoryOptions}
                      onChange={(value) => setProfileCategoryFilter(value ?? "")}
                    />
                    <Select
                      placeholder="Collection"
                      clearable
                      value={profileCollectionFilter || null}
                      data={profileCollectionOptions}
                      onChange={(value) => setProfileCollectionFilter(value ?? "")}
                      disabled={!profileCategoryFilter}
                    />
                  </Group>
                  <ScrollArea h={260}>
                    <SimpleGrid cols={2} spacing="xs">
                      {filteredFurniture.map((item) => (
                        <Card
                          key={item.id}
                          p="xs"
                          radius="md"
                          style={{ border: "1px solid rgba(0,0,0,0.08)" }}
                        >
                          <Box
                            style={{
                              height: 84,
                              borderRadius: 8,
                              overflow: "hidden",
                              border: "1px solid rgba(0,0,0,0.08)",
                              background:
                                "radial-gradient(ellipse at 50% 80%, rgba(253,126,20,0.04) 0%, transparent 60%), linear-gradient(180deg, #edf2f7 0%, #f8f9fa 100%)",
                            }}
                          >
                            <FurniturePreviewCanvas
                              modelUrl={item.modelUrl}
                              tintColor={item.tintColor}
                              colorVariants={item.colorVariants}
                              defaultColorVariantId={item.defaultColorVariantId}
                              colorVariantId={item.defaultColorVariantId}
                            />
                          </Box>
                          <Text size="xs" fw={600} mt={6} truncate>
                            {item.name}
                          </Text>
                          <Group gap={4} mt={4}>
                            <Badge size="xs" variant="outline">
                              {resolveCatalogCategoryName(
                                item.catalogCategory ??
                                  DEFAULT_FURNITURE_CATALOG_CATEGORY_KEY
                              )}
                            </Badge>
                            <Badge size="xs" variant="outline" color="grape">
                              {resolveCatalogCollectionName(
                                item.catalogCollection ?? ""
                              )}
                            </Badge>
                            {item.itemState === "draft" ? (
                              <Badge size="xs" color="orange" variant="light">
                                Draft
                              </Badge>
                            ) : null}
                          </Group>
                        </Card>
                      ))}
                    </SimpleGrid>
                    {filteredFurniture.length === 0 ? (
                      <Text size="sm" c="dimmed" mt={8}>
                        No furniture in this selection.
                      </Text>
                    ) : null}
                  </ScrollArea>
                </Stack>
              ) : null}
            </Stack>
          </Card>
        </SimpleGrid>
      </Stack>
    </Container>
  );
}
